import { ReactElement } from 'react'
import type { SvgIconComponent } from '@mui/icons-material'
import { Box, TextField, useTheme } from '@mui/material'
import { MaterialSymbol } from '@ui2/icons'
import type { SortDir, SortMode } from '../types'
import { ListFilterButton, type ElementListFilters, type QsFilterKind } from './ListFilterButton'
import { SortGroup } from './SortGroup'

export type ListToolbarProps = {
  filterText: string
  onFilterTextChange: (next: string) => void
  filters: ElementListFilters
  onFiltersChange: (next: ElementListFilters) => void
  crossSectionOptions: string[]
  showExportedFilter: boolean
  /** Offer the Schubanker list's foot/head filter. */
  showShearAnchorPositionFilter?: boolean
  sortMode: SortMode
  sortDir: SortDir
  onSortModeChange: (next: SortMode) => void
  groupingEnabled: boolean
  /** Tooltip of the trailing-metric sort option; omit to hide it. */
  metricSortLabel?: string
  /** Set false for lists whose rows have no η_c,90 (anchors). */
  etaCSortEnabled?: boolean
  /** Renames the `qs`-column sort option (anchors sort by the chosen anchor, not a cross section). */
  qsSortOption?: { label: string; icon: SvgIconComponent }
  /** Names the `qs` filter after what the column holds. */
  qsFilterKind?: QsFilterKind
  sortModes?: SortMode[]
}

/** Top toolbar of the element list: filter popover, text search and sort mode toggle. */
export const ListToolbar = ({
  filterText,
  onFilterTextChange,
  filters,
  onFiltersChange,
  crossSectionOptions,
  showExportedFilter,
  showShearAnchorPositionFilter,
  sortMode,
  sortDir,
  onSortModeChange,
  groupingEnabled,
  metricSortLabel,
  etaCSortEnabled,
  qsSortOption,
  qsFilterKind,
  sortModes,
}: ListToolbarProps): ReactElement => {
  const theme = useTheme()

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <ListFilterButton
        filters={filters}
        onChange={onFiltersChange}
        crossSectionOptions={crossSectionOptions}
        showExported={showExportedFilter}
        qsFilterKind={qsFilterKind}
        showShearAnchorPosition={showShearAnchorPositionFilter}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TextField
          size="small"
          value={filterText}
          onChange={e => onFilterTextChange(e.target.value)}
          placeholder="Suchen…"
          fullWidth
          InputProps={{
            startAdornment: (
              <Box sx={{ display: 'inline-flex', mr: 0.75, color: theme.palette.text.secondary }}>
                <MaterialSymbol icon="search" size={16} />
              </Box>
            ),
            sx: { fontSize: 13, height: 30 },
          }}
        />
      </Box>
      <SortGroup
        modes={sortModes}
        value={sortMode}
        direction={sortDir}
        onChange={onSortModeChange}
        groupingEnabled={groupingEnabled}
        metricSortLabel={metricSortLabel}
        etaCSortEnabled={etaCSortEnabled}
        qsSortOption={qsSortOption}
      />
    </Box>
  )
}
